import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { de } from "date-fns/locale/de";

import { useGetUserLogs } from "@/firebase/logs/use.get.user.logs";
import { selectUserState } from "@/store/slices/user.slice";
import { selectCurrentStage } from "@/store/slices/stages.slice";
import { useAppSelector } from "@/hooks/common";
import { StyledButton } from "@/components/button";
import { AppModal, AppModalProps } from "@/components/modals/app.modal";
import { AppRoutes } from "@/constants/app.routes";

import { handleCorrectAnswer } from "../shared/handle.correct.answer";
import { handleIncorrectAnswer } from "../shared/handle.incorrect.answer";
import { ContentWrapper, QuestionText, DatePickerWrapper } from "./styles";

export const DateSelectView = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [modal, setModal] = useState<AppModalProps>({
    open: false,
    title: "",
    titleColor: "green",
    body: "",
    buttonText: "",
    onClose: () => {
      setModal((prev) => ({
        ...prev,
        open: false,
      }));
    },
  });

  const user = useAppSelector(selectUserState).user!;
  const { userLogs } = useGetUserLogs(user.phone_number);
  const currentStage = useAppSelector(selectCurrentStage)!;
  const navigate = useNavigate();

  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const onShowSolution = (answer: string | string[]) => {
    // Parse the answer date string (DD.MM.YYYY) and set it
    const dateString = Array.isArray(answer) ? answer[0] : answer;
    const parts = dateString.split(".");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const year = parseInt(parts[2], 10);
      setSelectedDate(new Date(year, month, day));
    }
  };

  const handleSubmitAnswer = () => {
    if (!selectedDate) return;

    const formattedDate = formatDate(selectedDate);
    let isCorrect = false;

    if (currentStage.answer_type === "date_input") { // For correct TS typings
      isCorrect = currentStage.answers.includes(formattedDate);
    }

    if (isCorrect) {
      // Handle correct answer
      handleCorrectAnswer({
        solvedStage: currentStage,
        userAnswer: formattedDate,
        navigate,
        setModal,
      });
    } else {
      // Handle incorrect answer
      handleIncorrectAnswer({
        currentStage,
        userAnswer: formattedDate,
        userLogs,
        setModal,
        onShowSolution,
      });
    }
  };

  return (
    <ContentWrapper>
      <QuestionText>
        {currentStage.question}
      </QuestionText>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
        <DatePickerWrapper>
          <DatePicker
            label="Datum auswählen"
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            format="dd.MM.yyyy"
            views={["year", "month", "day"]}
            openTo="day"
            referenceDate={new Date(2024, 6, 1)}
            slotProps={{
              textField: {
                variant: "outlined",
                fullWidth: true,
              },
            }}
          />
        </DatePickerWrapper>
      </LocalizationProvider>

      <Box sx={{ mt: 6.4, textAlign: "center" }}>
        <StyledButton
          variant="contained"
          disabled={selectedDate === null}
          onClick={handleSubmitAnswer}
        >
          ANTWORT ÜBERPRÜFEN
        </StyledButton>
      </Box>

      <Box mt={2} textAlign="center">
        <StyledButton to={AppRoutes.select_stage} component={Link}>
          Abbrechen
        </StyledButton>
      </Box>

      {/* Modal */}
      <AppModal {...modal} />
    </ContentWrapper>
  );
};

